
<script setup>
import VueMultiselect from "vue-multiselect";
import {computed, reactive, ref, watch, watchEffect} from "vue";
import RedemptionSpecificDatePeriod from "./RedemptionSpecificDatePeriod.vue";
import RedemptionTimesPeriod from "./RedemptionTimesPeriod.vue";
import Notification from "../../../../../elements/Notification.vue";
import Tooltip from "../../../../../elements/Tooltip.vue";
import Icon from "../../../../../elements/Icon.vue";
import Btn from "../../../../../elements/Btn.vue";
import TextField from "../../../../../elements/forms/TextField.vue";
import {useCouponStore} from "../../../../../stores/app/programs/common/coupons/CouponStore";

const props = defineProps(
        {
            couponId: [Number],
            couponName: [String],
            couponDiscountAmount: [String],
            program: Object,
            couponAction: {
                String,
                default: 'create'
            }
        }
    )

const isReferAFriendProgram = props.program?.program_type?.type === 'refer_a_friend'
const isNewsletterProgram = props.program?.program_type?.type === 'newsletter'

const couponStore = useCouponStore()

const isProviderWooCommerce = computed(() => couponStore.syncProvider === 'woocommerce')

    watchEffect(() => {
        if (isProviderWooCommerce.value && couponStore.promotionCodes.length === 0 && !couponStore.getIsAutogenerateRewardCoupon) {
            addNewPromoCode();
        }

        if (couponStore.getIsAutogenerateRewardCoupon) {
            couponStore.autoAssignPromotionCode.auto_generate_promotion_code = true;
        }
    });

    watch(() => couponStore.getIsAutogenerateRewardCoupon, (newValue) => {
        if(newValue) {
            enableAutoAssignPromoCode();
            couponStore.promotionCodes = [];
        } else {
            disableAutoAssignPromoCode();
        }
    });

const addAutoAssignPromoCodeMetadata = () => {
    couponStore.autoAssignPromotionCode.metadata.push({ key: '', value: '' });
}

const removeAutoAssignPromoCodeMetadata = (metadataIndex) => {
    couponStore.autoAssignPromotionCode.metadata.splice(metadataIndex, 1);
}

const autoGeneratePromotionCodeNameBuilderAvailableFields = computed(() => [
    { value: 'name', label: couponStore.getIsAutogenerateRewardCoupon ? 'Customer name' : 'Partner name', example: 'Jack'},
    { value: 'surname', label: couponStore.getIsAutogenerateRewardCoupon ? 'Customer last name' : 'Partner last name', example: 'Partnero'},
    { value: 'email', label: couponStore.getIsAutogenerateRewardCoupon ? 'Customer email address' : 'Partner email address', example: 'jack@partnero.com'},
    { value: 'discount_amount', label: 'Discount amount', example: '10'},
    { value: 'coupon_name', label: 'Coupon name', example: 'DISCOUNT2024'},
    { value: 'partner_identifier', label: couponStore.getIsAutogenerateRewardCoupon ? 'Customer key' : 'Partner key', example: '29UN6Em3iVD6'},
    { value: 'random_characters', label: 'Random characters', example: 'AdXjwO4D'},
]);

const autoGeneratePromotionCodeNameFieldRandomSymbolsCountOptions = [
    { value: 3, label: '3 characters' },
    { value: 4, label: '4 characters' },
    { value: 5, label: '5 characters' },
    { value: 6, label: '6 characters' },
    { value: 7, label: '7 characters' },
];

const enableAutoAssignPromoCode = () => {
    couponStore.autoAssignPromotionCode.auto_generate_promotion_code = true;
}

const disableAutoAssignPromoCode = () => {
    couponStore.autoAssignPromotionCode.auto_generate_promotion_code = false;
}

const autoGeneratePromotionCodeNameFieldExample = () => {
    let example = '';

    if (couponStore.autoAssignPromotionCode.auto_generate_promotion_code_name_fields.length === 0) {
        return couponStore.autoAssignPromotionCode.auto_generate_promotion_code_name_custom_prefix + '0brAnRUB';
    }

    couponStore.autoAssignPromotionCode.auto_generate_promotion_code_name_fields.forEach(field => {
        switch (field) {
            case 'name':
                example += getFieldExample('name');
                break;
            case 'surname':
                example += getFieldExample('surname');
                break;
            case 'email':
                example += getFieldExample('email').split('@')[0];
                break;
            case 'discount_amount':
                example += props.couponDiscountAmount;
                break;
            case 'coupon_name':
                example += props.couponName;
                break;
            case 'random_characters':
                example += getFieldExample('random_characters').substring(0, couponStore.autoAssignPromotionCode.auto_generate_promotion_code_name_random_characters_count);
                break;
            case 'partner_identifier':
                example += getFieldExample('partner_identifier');
                break;
        }
    });

    return couponStore.autoAssignPromotionCode.auto_generate_promotion_code_name_custom_prefix + example;
}

const getFieldExample = (fieldValue) => {
    return autoGeneratePromotionCodeNameBuilderAvailableFields.value.find(field => field.value === fieldValue).example;
}

    const notification = reactive({
        showNotification: false,
        notificationTitle: 'Error',
        notificationType: 'warning',
        notificationMessage: 'Action error, see console for error details.'
    });

    const isLoadingPartners = ref(false)
    const isLoadingCustomers = ref(false)
    const isLoadingSubscribers = ref(false)
    const specific_partners_list = ref([])
    const specific_customers_list = ref([])
    const specific_subscribers_list = ref([])

    const addNewPromoCode = () => {
        couponStore.promotionCodes.push({
            id:'',
            code: '',
            edit: true,
            active: true,
            first_time_order: false,
            limit_to_specific_partner: false,
            coupon_specific_partners: [],
            limit_to_specific_customer: false,
            coupon_specific_customers: [],
            limit_to_specific_subscriber: false,
            coupon_specific_subscribers: [],
            minimum_order_status: '0',
            minimum_order_value: '',
            expiration_date_status: '0',
            expiration_date_value: '',
            redemption_times_status: '0',
            redemption_times_value: '',
            metadata: [],
        })
    }

    const removePromoCode = (index) => {
        couponStore.promotionCodes.splice(index, 1);
    }

    const addMetadata = (index) => {
        couponStore.promotionCodes[index].metadata.push({ key: '', value: '' });
    }

    const removeMetadata = (promoCodeIndex, metadataIndex) => {
        couponStore.promotionCodes[promoCodeIndex].metadata.splice(metadataIndex, 1);
    }

    let delayTimer = null;

    function doSearch(query, searchingFor) {
        clearTimeout(delayTimer);
        delayTimer = setTimeout(function () {
            if (searchingFor === 'partners') {
                asyncFindPartners(query);
            }
            else if (searchingFor === 'customers' && isReferAFriendProgram) {
                asyncFindReferrals(query);
            } else if (searchingFor === 'customers') {
                asyncFindCustomers(query);
            } else if (searchingFor === 'subscribers') {
                asyncFindSubscribers(query);
            }
        }, 700);
    }
    const asyncFindCustomers = (query) => {
        isLoadingCustomers.value = true
        axios.get(route('app_int.promotion-codes.customers.search', props.program) + '?search=' + query)
            .then(response => {
                specific_customers_list.value = response.data
                isLoadingCustomers.value = false
            })
            .finally(() => {
                isLoadingCustomers.value = false
            })
    }

    const asyncFindReferrals = (query) => {
        isLoadingCustomers.value = true
        axios.get(route('app_int.promotion-codes.partners.search', props.program) + '?search=' + query)
            .then(response => {
                specific_customers_list.value = response.data
                isLoadingCustomers.value = false
            })
            .finally(() => {
                isLoadingCustomers.value = false
            })
    }

    const asyncFindSubscribers = (query) => {
        isLoadingSubscribers.value = true
        axios.get(route('app_int.promotion-codes.subscribers.search', props.program) + '?search=' + query)
            .then(response => {
                specific_subscribers_list.value = response.data
                isLoadingSubscribers.value = false
            })
            .finally(() => {
                isLoadingSubscribers.value = false
            })
    }

    const asyncFindPartners = (query) => {
        isLoadingPartners.value = true
        axios.get(route('app_int.promotion-codes.partners.search', props.program) + '?search=' + query)
            .then(response => {
                specific_partners_list.value = response.data
                isLoadingPartners.value = false
            })
            .finally(() => {
                isLoadingPartners.value = false
            })
    }

    const limitText = (count) => {
        return `and ${count} other customers`
    }

    const jsonPromoCodes = computed(() => {
        return JSON.stringify(couponStore.promotionCodes)
    })

    const jsonAutoAssignPromoCode = computed(() => {
        return JSON.stringify(couponStore.autoAssignPromotionCode)
    })

    const promotionCodeExpired = (date) => {
        return date && new Date(date) < new Date()
    }
</script>

<template>
    <div>
        <notification
            :show="notification.showNotification"
            :title="notification.notificationTitle"
            :type="notification.notificationType"
            class="mb-20"
            @notificationClosed="notification.showNotification = false"
            whitespace="whitespace-normal"
        >
            <template #message>
                {{ notification.notificationMessage }}
            </template>
        </notification>
        <div class="space-y-2">

            <div class="border rounded p-4 space-y-2" v-if="!couponStore.promotionCodes.length && !couponStore.autoAssignPromotionCode.auto_generate_promotion_code">
                <div class="text-sm text-gray-500">
                    Create customizable promotion codes for coupons, allowing for multiple user-redeemable codes with defined eligibility and limits.
                </div>
                <btn type="secondary" size="small" @click.prevent="addNewPromoCode">
                    Add a promotion code
                </btn>
            </div>

            <!-- Classic promo codes -->
            <div
                v-for="(promoCode, promoCodeIndex) in couponStore.promotionCodes" :key="'promoCode-' + promoCodeIndex + '-' + promoCode.id"
                class="border rounded"
            >

                <div class="flex items-center justify-between p-4">

                    <div class="flex items-center">
                        <div>
                            <icon name="coupon" size="24" class="hover:text-gray-500" />
                        </div>
                        <div class="text-sm font-semibold text-gray-900 ml-4">
                            {{ promoCode.code }}
                            <span v-if="promotionCodeExpired(promoCode?.expiration_date_value)"
                                class="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs border align-middle font-medium bg-pink-100 text-pink-800 border-pink-100"
                            >
                                Expired
                            </span>
                        </div>
                    </div>
                    <div class="flex items-center gap-2">
                        <button v-if="!isProviderWooCommerce"
                                type="button"
                                class="rounded border hover:bg-gray-50 hover:bg-opacity-75 p-2 focus:outline-none"
                                @click.prevent="removePromoCode(promoCodeIndex)"
                        >
                            <icon size="14" name="delete"></icon>
                        </button>
                        <button type="button"
                                class="rounded border hover:bg-gray-50 hover:bg-opacity-75 p-2 focus:outline-none"
                                @click.prevent="promoCode.edit = !promoCode.edit"
                        >
                            <icon size="14" name="settings" v-if="!promoCode.edit"></icon>
                            <icon size="14" name="check-line" v-if="promoCode.edit"></icon>
                        </button>
                    </div>

                </div>

                <div v-if="promoCode.edit" class="bg-gray-50 bg-opacity-75 border-t space-y-4 p-4">

                    <div class="grid grid-cols-4 gap-4">
                        <text-field
                            class="col-span-3"
                            label="Code"
                            type="text"
                            v-model="promoCode.code"
                            placeholder="E.g. DISCOUNT30"
                        >
                            <template #note>
                                This code must be unique across all active promotion codes.
                                If left blank we will generate code automatically.
                            </template>
                            <template #append>
                                <div class="inline-flex items-center pl-1 text-gray-500 cursor-pointer">
                                    <tooltip content="Only alpha numeric, no special characters allowed. The field is case-insensitive.">
                                        <icon
                                            name="info"
                                            size="16"
                                        />
                                    </tooltip>
                                </div>
                            </template>
                        </text-field>
                    </div>

                    <toggle
                        v-model="promoCode.first_time_order"
                        title="Eligible for first-time order only"
                    >
                        <template #description>
                            If enabled, coupon will be valid only for first time order i.e. for customers who have never made a purchase.
                        </template>
                    </toggle>

                    <!-- Available only for affiliate -->
                    <toggle v-if="!isReferAFriendProgram && !isNewsletterProgram"
                        v-model="promoCode.limit_to_specific_partner"
                        title="Limit to specific partner"
                    >
                        <template #description>
                            If enabled, to set the specific partner that is eligible for discounts, add the relevant partner in the Apply to specific partners field.
                        </template>
                    </toggle>

                    <div class="mt-5 mb-5" v-if="promoCode.limit_to_specific_partner">
                        <VueMultiselect
                            v-model="promoCode.coupon_specific_partners"
                            label="full_name"
                            track-by="id"
                            placeholder="Choose a partner"
                            :options="specific_partners_list"
                            :multiple="false"
                            :searchable="true"
                            :loading="isLoadingPartners"
                            :internal-search="false"
                            :clear-on-select="false"
                            :close-on-select="false"
                            :options-limit="300"
                            :limit="1"
                            :limit-text="limitText"
                            :max-height="600"
                            :show-no-results="true"
                            @search-change="doSearch($event, 'partners')"
                        >
                            <span slot="noResult">Oops! No partners found. Consider changing the search query.</span>
                        </VueMultiselect>
                    </div>

                    <!-- Available for affiliate and refer a friend programs -->
                    <toggle v-if="!isNewsletterProgram"
                        v-model="promoCode.limit_to_specific_customer"
                            title="Limit to specific customer"
                    >
                        <template #description>
                            If enabled, to set the specific customers that is eligible for discounts, add the relevant customers in the Apply to specific customers field.
                        </template>
                    </toggle>

                    <div class="mt-5 mb-5" v-if="promoCode.limit_to_specific_customer">
                        <VueMultiselect
                            v-model="promoCode.coupon_specific_customers"
                            label="customer_full_name"
                            track-by="id"
                            placeholder="Choose a customer"
                            :options="specific_customers_list"
                            :multiple="false"
                            :searchable="true"
                            :loading="isLoadingCustomers"
                            :internal-search="false"
                            :clear-on-select="false"
                            :close-on-select="false"
                            :options-limit="300"
                            :limit="2"
                            :limit-text="limitText"
                            :max-height="600"
                            :show-no-results="true"
                            @search-change="doSearch($event, 'customers')"
                        >
                            <span slot="noResult">Oops! No customers found. Consider changing the search query.</span>
                        </VueMultiselect>
                    </div>

                    <!-- Available for nrp only -->
                    <toggle v-if="isNewsletterProgram"
                        v-model="promoCode.limit_to_specific_subscriber"
                            title="Limit to specific subscriber"
                    >
                        <template #description>
                            If enabled, to set the specific subscriber that is eligible for discounts, add the relevant subscriber in the Apply to specific subscriber field.
                        </template>
                    </toggle>

                    <div class="mt-5 mb-5" v-if="promoCode.limit_to_specific_subscriber">
                        <VueMultiselect
                            v-model="promoCode.coupon_specific_subscribers"
                            label="subscriber_full_name"
                            track-by="id"
                            placeholder="Choose a subscriber"
                            :options="specific_subscribers_list"
                            :multiple="false"
                            :searchable="true"
                            :loading="isLoadingSubscribers"
                            :internal-search="false"
                            :clear-on-select="false"
                            :close-on-select="false"
                            :options-limit="300"
                            :limit="2"
                            :limit-text="limitText"
                            :max-height="600"
                            :show-no-results="true"
                            @search-change="doSearch($event, 'subscribers')"
                        >
                            <span slot="noResult">Oops! No subscribers found. Consider changing the search query.</span>
                        </VueMultiselect>
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label for="promo_code_min_status" class="block mb-1.5 text-sm">
                                Minimum order value
                            </label>
                            <div
                                class="grid gap-1"
                                :class="promoCode.minimum_order_status === '0' ? 'grid-cols-1' : 'grid-cols-2'"
                            >
                                <select
                                    id="promo_code_min_status"
                                    v-model="promoCode.minimum_order_status"
                                    class="block w-full rounded border-gray-300 sm:text-sm focus:ring-0 focus:border-gray-400"
                                >
                                    <option value="0">OFF</option>
                                    <option value="1">ON</option>
                                </select>
                                <text-field
                                    v-if="promoCode.minimum_order_status !== '0'"
                                    v-model="promoCode.minimum_order_value"
                                    :disabled="promoCode.minimum_order_status === '0'"
                                    :required="promoCode.minimum_order_status !== '0'"
                                >
                                </text-field>
                            </div>
                            <small class="text-gray-500 mt-1 block">
                                Set an minimum amount that is eligible to use a promotion code.
                            </small>
                        </div>
                    </div>

                    <div class="grid grid-cols-2 gap-8">
                        <redemption-specific-date-period
                            :id="'spec-date-period-promo-code-' + promoCodeIndex"
                            name="Expiration date"
                            description="Limit the date when customers can redeem this promotion code."
                            v-model:type="promoCode.expiration_date_status"
                            v-model:redemption_value="promoCode.expiration_date_value"
                        />
                        <redemption-times-period
                            name="Number of times"
                            description="Limit the total number of times this code can be redeemed."
                            v-model:type="promoCode.redemption_times_status"
                            v-model:redemption_value="promoCode.redemption_times_value"
                        />
                    </div>

                    <hr/>

                    <div>
                        <label class="block mb-1.5 text-sm">
                            Metadata
                        </label>
                        <div
                            v-for="(metadata, metadataIndex) in promoCode.metadata"
                            :key="'promoCode-' + promoCodeIndex + '-metadata-' + metadataIndex"
                            class="grid grid-cols-5 gap-4 mb-2"
                        >
                            <text-field
                                class="md:col-span-2"
                                type="text"
                                v-model="metadata.key"
                                placeholder="Key"
                            >
                            </text-field>
                            <text-field
                                class="col-span-3"
                                type="text"
                                v-model="metadata.value"
                                placeholder="Value"
                            >
                                <template #append>
                                    <div class="inline-flex items-center pl-2 text-gray-500 cursor-pointer">
                                        <icon
                                            name="trash"
                                            size="18"
                                            @click="removeMetadata(promoCodeIndex, metadataIndex)"
                                        />
                                    </div>
                                </template>
                            </text-field>
                        </div>
                        <btn type="secondary"
                             icon-name="add-circle"
                             @click="addMetadata(promoCodeIndex)"
                             size="small"
                        >
                            Add metadata
                        </btn>
                    </div>
                </div>
            </div>
            <!-- ./ Classic promo codes -->

            <!-- Auto assign promo code -->
            <div v-if="couponStore.autoAssignPromotionCode.auto_generate_promotion_code" class="border rounded">
                <div class="flex items-center justify-between p-4">

                    <div class="flex items-center">
                        <div>
                            <icon name="coupon" size="24" class="hover:text-gray-500" />
                        </div>
                        <div class="text-sm font-semibold text-gray-900 ml-4">
                            {{ autoGeneratePromotionCodeNameFieldExample() }}
                        </div>
                    </div>

                    <div class="flex items-center">
                        <button
                            v-if="!couponStore.getIsAutogenerateRewardCoupon"
                            type="button"
                            class="focus:outline-none"
                            @click.prevent="disableAutoAssignPromoCode()"
                        >
                            <icon name="trash" size="20" color="red"/>
                        </button>
                    </div>
                </div>

                <div class="bg-gray-50 bg-opacity-75 border-t space-y-4 p-4 text-left">
                    <div class="space-y-4">
                        <div class="flex justify-between items-center border p-4 rounded text-sm">
                            <span class="flex flex-col flex-grow">
                                <span v-if="couponStore.getIsAutogenerateRewardCoupon" class="text-sm text-gray-500">
                                    Rewarded customers will receive their promotion code automatically.
                                </span>
                                <span v-else class="text-sm text-gray-500">
                                    Promotion codes will be automatically assigned to current partners, and newly registered partners will also receive a promotion code upon completing their registration.
                                </span>
                            </span>
                        </div>
                        <div>
                            <div class="block mb-2.5 text-sm">
                                Promotion code structure:
                            </div>
                            <div class="container mx-auto">
                                <div>

                                    <text-field
                                        placeholder="Prefix"
                                        type="text"
                                        v-model="couponStore.autoAssignPromotionCode.auto_generate_promotion_code_name_custom_prefix"
                                        class="w-1/2 pr-2 pb-2"
                                    >
                                    </text-field>
                                    <div class="flex items-end">
                                        <checkbox-group
                                            v-model="couponStore.autoAssignPromotionCode.auto_generate_promotion_code_name_fields"
                                            :options="autoGeneratePromotionCodeNameBuilderAvailableFields"
                                            layout="horizontal"
                                        />
                                        <div v-if="couponStore.autoAssignPromotionCode.auto_generate_promotion_code_name_fields.includes('random_characters')">
                                            <select
                                                v-model="couponStore.autoAssignPromotionCode.auto_generate_promotion_code_name_random_characters_count"
                                                class="block border-0 focus:ring-0 leading-5 rounded text-right text-xs text-gray-500 bg-transparent pl-6 pr-8 py-0"
                                            >
                                                <option :value="2">2 characters</option>
                                                <option v-for="option in autoGeneratePromotionCodeNameFieldRandomSymbolsCountOptions" :value="option.value">
                                                    {{ option.label }}
                                                </option>
                                            </select>
                                        </div>
                                    </div>

                                    <div class="mt-4">
                                        <label class="block text-gray-700 text-sm font-bold mb-2">Example:</label>
                                        <text-field
                                            type="text"
                                            :model-value="autoGeneratePromotionCodeNameFieldExample()"
                                            placeholder="Generated code"
                                            :disabled="true"
                                        >
                                            <template #note>
                                                If you do not select any field, the default promotion code name will be generated.
                                                For already taken names, the system will add an ordinal number to the codename.
                                            </template>
                                        </text-field>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div class="flex justify-between items-center">
                            <toggle v-model="couponStore.autoAssignPromotionCode.first_time_order"></toggle>
                            <span class="flex flex-col flex-grow" id="first_time_order-label">
                                        <span class="text-sm font-medium text-gray-900">
                                            Eligible for first-time order only
                                        </span>
                                        <span class="text-sm text-gray-500">
                                            If enabled, coupon will be valid only for first time order i.e. for customers who have never made a purchase.
                                        </span>
                                    </span>
                        </div>
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label for="promo_code_min_status" class="block mb-1.5 text-sm">
                                Minimum order value
                            </label>
                            <div
                                class="grid gap-1"
                                :class="couponStore.autoAssignPromotionCode.minimum_order_status === '0' ? 'grid-cols-1' : 'grid-cols-2'"
                            >
                                <select
                                    id="promo_code_min_status"
                                    v-model="couponStore.autoAssignPromotionCode.minimum_order_status"
                                    class="block w-full rounded border-gray-300 sm:text-sm focus:ring-0 focus:border-gray-400"
                                >
                                    <option value="0">OFF</option>
                                    <option value="1">ON</option>
                                </select>
                                <text-field
                                    v-if="couponStore.autoAssignPromotionCode.minimum_order_status !== '0'"
                                    v-model="couponStore.autoAssignPromotionCode.minimum_order_value"
                                    :disabled="couponStore.autoAssignPromotionCode.minimum_order_status === '0'"
                                    :required="couponStore.autoAssignPromotionCode.minimum_order_status !== '0'"
                                >
                                </text-field>
                            </div>
                            <small class="text-gray-500 mt-1 block">
                                Set an minimum amount that is eligible to use a promotion code.
                            </small>
                        </div>
                    </div>
                    <div class="grid grid-cols-2 gap-8">
                        <redemption-specific-date-period
                            id="spec-date-period-promo-code"
                            name="Expiration date"
                            description="Limit the date when customers can redeem this promotion code."
                            v-model:type="couponStore.autoAssignPromotionCode.expiration_date_status"
                            v-model:redemption_value="couponStore.autoAssignPromotionCode.expiration_date_value"
                        />
                        <redemption-times-period
                            name="Number of times"
                            description="Limit the total number of times this code can be redeemed."
                            v-model:type="couponStore.autoAssignPromotionCode.redemption_times_status"
                            v-model:redemption_value="couponStore.autoAssignPromotionCode.redemption_times_value"
                        />
                    </div>

                    <hr/>

                    <div>
                        <label class="block mb-1.5 text-sm">
                            Metadata
                        </label>
                        <div
                            v-for="(metadata, metadataIndex) in couponStore.autoAssignPromotionCode.metadata"
                            :key="'auto-assign-promo-code-metadata-' + metadataIndex"
                            class="grid grid-cols-5 gap-4 mb-2"
                        >
                            <text-field
                                class="md:col-span-2"
                                type="text"
                                v-model="metadata.key"
                                placeholder="Key"
                            >
                            </text-field>
                            <text-field
                                class="col-span-3"
                                type="text"
                                v-model="metadata.value"
                                placeholder="Value"
                            >
                                <template #append>
                                    <div class="inline-flex items-center pl-2 text-gray-500 cursor-pointer">
                                        <icon
                                            name="trash"
                                            size="18"
                                            @click="removeAutoAssignPromoCodeMetadata(metadataIndex)"
                                        />
                                    </div>
                                </template>
                            </text-field>
                        </div>
                        <btn type="secondary"
                             icon-name="add-circle"
                             @click="addAutoAssignPromoCodeMetadata()"
                             size="small"
                        >
                            Add metadata
                        </btn>
                    </div>
                </div>
            </div>
            <!-- ./ Auto assign promo code -->
        </div>

        <input type="text" hidden name="promotion_codes" v-model="jsonPromoCodes">
        <input type="text" hidden name="auto_assign_promotion_code" v-model="jsonAutoAssignPromoCode">
        <div class="flex text-left mt-6 space-x-3" v-if="(!(isProviderWooCommerce && couponStore.promotionCodes.length === 1) && !couponStore.getIsAutogenerateRewardCoupon)">
            <btn type="secondary" size="small" @click.prevent="addNewPromoCode"
                 v-if="couponStore.promotionCodes.length || couponStore.autoAssignPromotionCode.auto_generate_promotion_code"
            >
                Add a promotion code
            </btn>
            <btn v-if="(!isReferAFriendProgram && !isNewsletterProgram) && !couponStore.autoAssignPromotionCode.auto_generate_promotion_code" type="secondary" size="small" @click.prevent="enableAutoAssignPromoCode">
                Add auto-generated promotion code
            </btn>
        </div>
    </div>
</template>

<style>
div.multiselect__tags {
    min-height: 32px;
    display: block;
    padding: 3px 40px 3px 12px;
    border-radius: 5px;
    font-size: .875rem;
    border-color: #d1d5da;
}

div.multiselect__tags:active {
    border-color: #9da3ae;
}

.multiselect, .multiselect__input, .multiselect__single {
    font-size: .875rem !important;
}

.multiselect__input,
.multiselect__single {
    font-size: .875rem !important;
    margin-top: 6px !important;
    margin-bottom: 6px !important;
}

.multiselect--above.multiselect--active .multiselect__input {
    border: none !important;
    border-radius: 5px !important;
    background-color: #f1f1f1;
}

.multiselect__option {
    font-size: 12px;
}

.multiselect__tag {
    background: #15837B !important;
}
.multiselect__tags .multiselect__placeholder {
    color: #adadad;
    display: inline-block;
    margin-bottom: 6px;
    padding-top: 6px;
    font-size: .875rem;
}
</style>
